import { t } from "./i18n.js";
import { useEffect, useRef, useState } from "react";
import { deviceInfo, fetchTasks, getDiagKey, setDiagKey, uploadCapture, type DiagTask } from "./diag/client.js";
import { CameraScanner, type CaptureResolution, type CodeState } from "./scan/camera.js";

const RES_KEY = "trail.captureResolution";
const ZOOM_KEY = "trail.zoom";
function storedResolution(): CaptureResolution {
  try {
    const v = Number(localStorage.getItem(RES_KEY));
    return v === 1280 || v === 1920 || v === 3840 ? v : 3840;
  } catch {
    return 3840;
  }
}

export interface CameraViewProps {
  onCode: (scanData: string, format: string, mark: (scanData: string, state: CodeState) => void) => Promise<CodeState>;
  onClose: () => void;
  /** The scan-test page shows resolution, stats and the capture tools; the task client does not. */
  diagnostics?: boolean | undefined;
}

/** Full-screen viewfinder: codes are outlined as they are seen and turn green once accepted.
 *  With a diagnostics key set, shows the task list and a Capture button that uploads the
 *  current frame with everything the decoder saw. */
export function CameraView({ onCode, onClose, diagnostics = false }: CameraViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const scannerRef = useRef<CameraScanner | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [torch, setTorch] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [count, setCount] = useState(0);
  const [resolution, setResolution] = useState<CaptureResolution>(storedResolution);
  const [zoom, setZoom] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(ZOOM_KEY) ?? "1") || 1;
    } catch {
      return 1;
    }
  });
  const [zoomSupported, setZoomSupported] = useState(false);
  const zoomRef = useRef(zoom);
  const sessionStart = useRef(performance.now());
  const [stats, setStats] = useState<string>("");
  const [tasks, setTasks] = useState<DiagTask[]>([]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const [diag, setDiag] = useState(() => getDiagKey() !== "");
  const askKey = () => {
    const key = window.prompt("Diagnostics key");
    if (key && key.trim()) {
      setDiagKey(key.trim());
      setDiag(true);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    const overlay = overlayRef.current;
    if (!video || !overlay) return;
    let accepted = 0;
    const scanner = new CameraScanner({
      video,
      overlay,
      resolution,
      zoom: zoomRef.current,
      onCode: async (data, format) => {
        const state = await onCode(data, format, (d, s) => {
          scanner.markState(d, s);
          if (s === "accepted") setCount(++accepted);
        });
        if (state === "accepted") setCount(++accepted);
        return state;
      },
    });
    scannerRef.current = scanner;
    scanner
      .start()
      .then(() => {
        setTorchSupported(scanner.torchSupported);
        setZoomSupported((scanner.maxZoom ?? 1) >= 2);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    const statsTimer = window.setInterval(() => {
      const s = scanner.stats();
      setStats(`${s.resolution}${s.zoom && s.zoom !== 1 ? ` ×${s.zoom}` : ""} · ${s.fps} fps · ${s.path} ${s.captureMs ?? "–"} ms · locate ${s.lastLocateMs ?? "–"} ms · frame ${s.lastDecodeMs ?? "–"} ms (avg ${s.avgDecodeMs ? Math.round(s.avgDecodeMs) : "–"}) · ${s.regions} regions${s.lastError ? ` · ⚠ ${s.lastError}` : ""}`);
    }, 500);
    if (diag) fetchTasks().then(setTasks).catch(() => setTasks([]));
    return () => {
      window.clearInterval(statsTimer);
      scanner.stop();
    };
  }, [onCode, diag, resolution]);

  const changeResolution = (r: CaptureResolution) => {
    try {
      localStorage.setItem(RES_KEY, String(r));
    } catch {
      /* private mode */
    }
    setResolution(r);
  };

  const toggleZoom = async () => {
    const next = zoom === 1 ? 2 : 1;
    setZoom(next);
    zoomRef.current = next;
    try {
      localStorage.setItem(ZOOM_KEY, String(next));
    } catch {
      /* private mode */
    }
    await scannerRef.current?.setZoom(next);
  };

  const toggleTorch = async () => {
    const next = !torch;
    setTorch(next);
    await scannerRef.current?.setTorch(next);
  };

  const capture = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    setUploading("uploading…");
    try {
      const image = scanner.snapshot();
      const stored = await uploadCapture({
        taskId,
        note,
        image,
        meta: {
          device: deviceInfo(),
          stats: scanner.stats(),
          decodeTimes: [...scanner.decodeTimes],
          locateTimes: [...scanner.locateTimes],
          codes: scanner.codes().map((c) => ({ scanData: c.scanData.replaceAll("\u001d", "<GS>"), format: c.format, state: c.state, position: c.position })),
          accepted: count,
          sessionMs: Math.round(performance.now() - sessionStart.current),
          history: scanner.history.map((h) => ({ ...h, scanData: h.scanData.replaceAll("\u001d", "<GS>") })),
          torch,
          capturedAt: new Date().toISOString(),
        },
      });
      const inView = scanner.codes();
      const green = inView.filter((c) => c.state === "accepted").length;
      setUploading(`saved · ${green}/${inView.length} in view green · ${count} this session · ${stored.slice(11, 19)}`);
      setNote("");
    } catch (e) {
      setUploading(`failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    window.setTimeout(() => setUploading(null), 4000);
  };

  const task = tasks.find((t) => t.id === taskId) ?? null;

  return (
    <div className="camera">
      <video ref={videoRef} className="camera-video" playsInline muted autoPlay />
      <canvas ref={overlayRef} className="camera-overlay" />
      <div className="camera-top">
        {diagnostics && <span className="camera-stats">{stats}</span>}
        {diagnostics && (
          <select className="camera-task" value={resolution} onChange={(e) => changeResolution(Number(e.target.value) as CaptureResolution)} aria-label="Capture resolution">
            <option value={1280}>720p</option>
            <option value={1920}>1080p</option>
            <option value={3840}>4K</option>
          </select>
        )}
        {diagnostics && diag && tasks.length > 0 && (
          <select className="camera-task" value={taskId ?? ""} onChange={(e) => setTaskId(e.target.value || null)}>
            <option value="">— choose a task —</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>{t.title}</option>
            ))}
          </select>
        )}
        {task && <p className="camera-task-detail">{task.detail}</p>}
      </div>
      <div className="camera-bar">
        <span className="camera-count">{t("camera.accepted", { n: count })}</span>
        {zoomSupported && (
          <button type="button" className="secondary" onClick={() => void toggleZoom()}>
            {zoom === 1 ? t("camera.zoom2") : t("camera.zoom1")}
          </button>
        )}
        {torchSupported && (
          <button type="button" className="secondary" onClick={() => void toggleTorch()}>
            {torch ? t("camera.torchOff") : t("camera.torchOn")}
          </button>
        )}
        {diagnostics && (diag ? (
          <button type="button" className="secondary" onClick={() => void capture()} disabled={uploading !== null && uploading === "uploading…"}>
            {uploading ?? "Capture"}
          </button>
        ) : (
          <button type="button" className="secondary" onClick={askKey}>Diagnostics…</button>
        ))}
        <button type="button" className="camera-done" onClick={onClose}>{t("camera.done")}</button>
      </div>
      {diagnostics && diag && (
        <input className="camera-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for this capture (optional)" />
      )}
      {error && <div className="status bad camera-error">Camera unavailable: {error}</div>}
    </div>
  );
}
